import { HTMLAttributes, ReactChild, useCallback, useState } from "react";

export interface UseLiveRegionOptions extends Pick<HTMLAttributes<never>, "aria-live" | "aria-atomic"> {
    /**
     * If true, the live region's content will be cleared out no matter what the content prop is set to.
     * This can be useful to turn the live region off when you need to mute it temporarily.
     */
    isDisabled?: boolean;
}

export interface UseLiveRegionResult {
    /**
     * When called, will clear out the live region. Can be useful when turning the region off
     * and on if you want the same message to be read again. In this case, run clear, then run speak
     * in a requestAnimationFrame to bump the change out over React's render cycle.
     */
    clear: () => void;

    /**
     * The set of props you should apply to a component that renders the live regions.
     * We recommend spreading these onto a VisuallyHidden component.
     */
    getLiveRegionProps: (overrideProps?: HTMLAttributes<HTMLElement>) => HTMLAttributes<HTMLElement>;

    /**
     * When called, will force a re-render of the hook and pass the content
     * downstream via getLiveRegionProps().
     */
    speak: (newContent: ReactChild) => void;
}

export const useLiveRegion = ({
    "aria-atomic": ariaAtomic = true,
    isDisabled = false,
    "aria-live": ariaLive = "polite",
}: UseLiveRegionOptions = {}) => {
    const [children, setChildren] = useState<ReactChild>("");
    return {
        clear: useCallback(() => setChildren(""), []),
        speak: setChildren,
        getLiveRegionProps: (overrideProps: HTMLAttributes<HTMLElement> = {}) => {
            return {
                role: "status",
                "aria-live": isDisabled ? "off" : ariaLive,
                "aria-atomic": ariaAtomic,
                ...overrideProps,
                children: children,
            };
        },
    };
};
